import React, { useCallback, useReducer } from "react"
import boardContext from "./board-context"
import { BOARD_ACTIONS, TOOL_ACTION_TYPES, TOOL_ITEMS } from "../constants"
import {
  createElement,
  getSvgPathFromStroke,
  isPointNearElement,
} from "../Components/utils/element"
import getStroke from "perfect-freehand"
import { updateCanvas } from "../utils/api"
import { getSocket } from "../utils/socket"

const syncCanvas = (elements) => {
  const canvasId = window.location.pathname.split("/").pop()
  if (!canvasId) return

  updateCanvas(canvasId, elements)

  const socket = getSocket()
  if (socket?.connected) {
    socket.emit("canvas:update", { canvasId, elements })
  }
}

const boardReducer = (state, action) => {
  switch (action.type) {
    case BOARD_ACTIONS.CHANGE_TOOL: {
      return {
        ...state,
        activeToolItem: action.payload.tool,
      }
    }
    case BOARD_ACTIONS.CHANGE_ACTION_TYPE: {
      return {
        ...state,
        toolActionType: action.payload.actionType,
      }
    }
    case BOARD_ACTIONS.DRAW_DOWN: {
      const { clientX, clientY, stroke, fill, size } = action.payload
      const newElement = createElement(
        state.elements.length,
        clientX,
        clientY,
        clientX,
        clientY,
        { type: state.activeToolItem, stroke, fill, size }
      )

      const prevElements = state.elements
      return {
        ...state,
        toolActionType:
          state.activeToolItem === TOOL_ITEMS.TEXT
            ? TOOL_ACTION_TYPES.WRITING
            : TOOL_ACTION_TYPES.DRAWING,
        elements: [...prevElements, newElement],
      }
    }
    case BOARD_ACTIONS.DRAW_MOVE: {
      if (state.elements.length === 0) return state
      const { clientX, clientY } = action.payload
      const newElements = [...state.elements]
      const index = state.elements.length - 1
      const { type } = newElements[index]
      switch (type) {
        case TOOL_ITEMS.LINE:
        case TOOL_ITEMS.RECTANGLE:
        case TOOL_ITEMS.CIRCLE:
        case TOOL_ITEMS.ARROW:
          const { x1, y1, stroke, fill, size } = newElements[index]
          const newElement = createElement(index, x1, y1, clientX, clientY, {
            type: state.activeToolItem,
            stroke,
            fill,
            size,
          })
          newElements[index] = newElement
          return { ...state, elements: newElements }
        case TOOL_ITEMS.BRUSH: {
          const element = newElements[index]

          const newPoints = [...element.points, { x: clientX, y: clientY }]

          newElements[index] = {
            ...element,
            points: newPoints,
            path: new Path2D(getSvgPathFromStroke(getStroke(newPoints))),
          }

          return { ...state, elements: newElements }
        }

        default:
          throw new Error("Type not recognised")
      }
    }
    case BOARD_ACTIONS.DRAW_UP: {
      const elementsCopy = state.elements.map((el) => ({ ...el }))
      const newHistory = state.history.slice(0, state.index + 1)
      newHistory.push(elementsCopy)

      syncCanvas(elementsCopy)

      return {
        ...state,
        history: newHistory,
        index: state.index + 1,
      }
    }
    case BOARD_ACTIONS.ERASE: {
      const { clientX, clientY } = action.payload
      const newElements = state.elements.filter(
        (el) => !isPointNearElement(el, clientX, clientY)
      )

      return {
        ...state,
        elements: newElements,
      }
    }
    case BOARD_ACTIONS.CHANGE_TEXT: {
      const index = state.elements.length - 1
      if (index < 0) return state

      const newElements = [...state.elements]
      newElements[index] = {
        ...newElements[index],
        text: action.payload.text,
      }

      const newHistory = state.history.slice(0, state.index + 1)
      newHistory.push(newElements.map((el) => ({ ...el })))

      syncCanvas(newElements)
      
      return {
        ...state,
        toolActionType: TOOL_ACTION_TYPES.NONE,
        elements: newElements,
        history: newHistory,
        index: state.index + 1,
      }
    }
    case BOARD_ACTIONS.UNDO: {
      const prevIndex = state.index - 1
      if (prevIndex < 0) return state

      const elements = state.history[prevIndex]
      syncCanvas(elements)

      return {
        ...state,
        elements,
        index: prevIndex,
      }
    }
    case BOARD_ACTIONS.REDO: {
      const nextIndex = state.index + 1
      if (nextIndex >= state.history.length) return state

      const elements = state.history[nextIndex]
      syncCanvas(elements)

      return {
        ...state,
        elements,
        index: nextIndex,
      }
    }
    case BOARD_ACTIONS.SET_ELEMENTS: {
      return {
        ...state,
        elements: action.payload.elements,
        history: [action.payload.elements],
        index: 0,
      }
    }
    default:
      return state
  }
}

const initialBoardState = {
  activeToolItem: TOOL_ITEMS.BRUSH,
  toolActionType: TOOL_ACTION_TYPES.NONE,
  elements: [],
  history: [[]],
  index: 0,
}

const BoardProvider = ({ children }) => {
  const [boardState, dispatchBoardAction] = useReducer(
    boardReducer,
    initialBoardState
  )

  const changeToolHandler = (tool) => {
    dispatchBoardAction({
      type: BOARD_ACTIONS.CHANGE_TOOL,
      payload: {
        tool,
      },
    })
  }

  const boardMouseDownHandler = (event, toolboxState) => {
    if (boardState.toolActionType === TOOL_ACTION_TYPES.WRITING) return
    const { clientX, clientY } = event
    if (boardState.activeToolItem === TOOL_ITEMS.ERASER) {
      dispatchBoardAction({
        type: BOARD_ACTIONS.CHANGE_ACTION_TYPE,
        payload: {
          actionType: TOOL_ACTION_TYPES.ERASING,
        },
      })
      return
    }

    dispatchBoardAction({
      type: BOARD_ACTIONS.DRAW_DOWN,
      payload: {
        clientX,
        clientY,
        stroke: toolboxState[boardState.activeToolItem]?.stroke,
        fill: toolboxState[boardState.activeToolItem]?.fill,
        size: toolboxState[boardState.activeToolItem]?.size,
      },
    })
  }

  const boardMouseMoveHandler = (event) => {
    if (boardState.toolActionType === TOOL_ACTION_TYPES.WRITING) return
    const { clientX, clientY } = event
    if (boardState.toolActionType === TOOL_ACTION_TYPES.DRAWING) {
      dispatchBoardAction({
        type: BOARD_ACTIONS.DRAW_MOVE,
        payload: {
          clientX,
          clientY,
        },
      })
    } else if (boardState.toolActionType === TOOL_ACTION_TYPES.ERASING) {
      dispatchBoardAction({
        type: BOARD_ACTIONS.ERASE,
        payload: {
          clientX,
          clientY,
        },
      })
    }
  }

  const boardMouseUpHandler = () => {
    if (boardState.toolActionType === TOOL_ACTION_TYPES.WRITING) return
    if (
      boardState.toolActionType === TOOL_ACTION_TYPES.DRAWING ||
      boardState.toolActionType === TOOL_ACTION_TYPES.ERASING
    ) {
      dispatchBoardAction({
        type: BOARD_ACTIONS.DRAW_UP,
      })
    }
    dispatchBoardAction({
      type: BOARD_ACTIONS.CHANGE_ACTION_TYPE,
      payload: {
        actionType: TOOL_ACTION_TYPES.NONE,
      },
    })
  }

  const textAreaBlurHandler = (text) => {
    dispatchBoardAction({
      type: BOARD_ACTIONS.CHANGE_TEXT,
      payload: {
        text,
      },
    })
  }

  const boardUndoHandler = useCallback(() => {
    dispatchBoardAction({
      type: BOARD_ACTIONS.UNDO,
    })
  }, [])

  const boardRedoHandler = useCallback(() => {
    dispatchBoardAction({
      type: BOARD_ACTIONS.REDO,
    })
  }, [])

  const loadElements = (elements) => {
    dispatchBoardAction({
      type: BOARD_ACTIONS.SET_ELEMENTS,
      payload: { elements },
    })
  }

  const boardContextValue = {
    activeToolItem: boardState.activeToolItem,
    elements: boardState.elements,
    toolActionType: boardState.toolActionType,
    changeToolHandler,
    boardMouseDownHandler,
    boardMouseMoveHandler,
    boardMouseUpHandler,
    textAreaBlurHandler,
    undo: boardUndoHandler,
    redo: boardRedoHandler,
    loadElements,
  }
  return (
    <boardContext.Provider value={boardContextValue}>
      {children}
    </boardContext.Provider>
  )
}

export default BoardProvider
